import { useEffect, useRef, useState } from 'react'
import type { InteractionProps } from './registry'
import { getNonEmptyString } from './shared/config'
import { asRecord } from './shared/response'
import controls from './shared/controls.module.css'
import styles from './LiquidityCrisisLab.module.css'
import {
  MAX_RUN,
  RUN_STEP,
  assessRun,
  explainRun,
  formatUsd,
  initialRunBank,
  isRunComplete,
  liquidBuffer,
} from './liquidityLab/runModel'

/**
 * Custom teaching interaction: a deterministic deposit-run sandbox. The learner
 * drives the size of a run on Cedar River's known balance sheet and watches the
 * outflow be met from cash, then from securities, then go unmet once the liquid
 * buffer is exhausted — all while economic equity (solvency) holds at +$2M. The
 * two gauges disagree past $5M: liquidity fails while solvency stands, making
 * the independence of the two conditions visible. All math lives in
 * `./liquidityLab/runModel`.
 */
export function LiquidityCrisisLab({
  config,
  savedResponse,
  onResponseChange,
  onComplete,
}: InteractionProps) {
  const bankName = getNonEmptyString(config, 'bank') ?? 'the bank'
  const task =
    getNonEmptyString(config, 'task') ??
    'Increase the deposit run until the bank can no longer pay — and watch what its equity does while that happens.'
  const allowHaircut = config.haircut === true

  const saved = asRecord(savedResponse)
  const [outflow, setOutflow] = useState<number>(() =>
    typeof saved.outflow === 'number' ? saved.outflow : 0,
  )
  const [haircut, setHaircut] = useState<boolean>(() =>
    allowHaircut && typeof saved.haircut === 'boolean' ? saved.haircut : false,
  )

  const bank = initialRunBank()
  const buffer = liquidBuffer(bank)
  const run = assessRun(outflow, { haircut }, bank)
  const failed = !run.canPay
  const completed = isRunComplete(outflow, bank)

  const completedRef = useRef(false)
  useEffect(() => {
    if (completed && !completedRef.current) {
      completedRef.current = true
      onComplete?.()
    }
  }, [completed, onComplete])

  function commitOutflow(next: number) {
    setOutflow(next)
    onResponseChange?.({ outflow: next, haircut })
  }

  function commitHaircut(next: boolean) {
    setHaircut(next)
    onResponseChange?.({ outflow, haircut: next })
  }

  const bufferUsedPct = Math.min(100, (run.outflow / buffer) * 100)
  const equityStartPct = (run.equity / bank.equity) * 100

  return (
    <section className={styles.lab} aria-label={`Deposit-run lab for ${bankName}`}>
      <div className={styles.controls}>
        <label className={styles.sliderLabel} htmlFor="run-slider">
          Deposit run this week
        </label>
        <div className={styles.sliderRow}>
          <button
            type="button"
            className={controls.button}
            onClick={() => commitOutflow(Math.max(0, run.outflow - RUN_STEP))}
            disabled={run.outflow <= 0}
            aria-label="Decrease the deposit run"
          >
            −
          </button>
          <input
            id="run-slider"
            className={styles.slider}
            type="range"
            min={0}
            max={MAX_RUN}
            step={RUN_STEP}
            value={run.outflow}
            onChange={(e) => commitOutflow(Number(e.target.value))}
            aria-valuetext={formatUsd(run.outflow)}
          />
          <button
            type="button"
            className={controls.button}
            onClick={() => commitOutflow(Math.min(MAX_RUN, run.outflow + RUN_STEP))}
            disabled={run.outflow >= MAX_RUN}
            aria-label="Increase the deposit run"
          >
            +
          </button>
        </div>
        <p className={styles.readout} aria-live="polite">
          Depositors demand <strong>{formatUsd(run.outflow)}</strong> back.
        </p>
      </div>

      <div className={styles.waterfall}>
        <h4 className={styles.panelHead}>How the run is met</h4>
        <Line label="Paid from cash" value={run.cashUsed} note={`of ${formatUsd(bank.cash)}`} />
        <Line
          label="Paid by selling securities"
          value={run.securitiesSold}
          note={`of ${formatUsd(bank.securities)}`}
        />
        <Line
          label="Unmet — loans can't be sold in time"
          value={run.shortfall}
          note={`${formatUsd(bank.loans)} locked in loans`}
          warn={run.shortfall > 0}
        />
      </div>

      <div className={styles.gauges}>
        <div className={`${styles.gauge} ${failed ? styles.gaugeBad : styles.gaugeGood}`}>
          <h4 className={styles.panelHead}>Liquidity — can it pay now?</h4>
          <div className={styles.meter} aria-hidden="true">
            <div
              className={`${styles.meterFill} ${failed ? styles.meterFillBad : ''}`}
              style={{ width: `${bufferUsedPct}%` }}
            />
          </div>
          <p className={styles.gaugeReadout}>
            Buffer {formatUsd(buffer)} · demanded {formatUsd(run.outflow)}
          </p>
          <p className={styles.verdict}>
            {failed
              ? `FAILS — short ${formatUsd(run.shortfall)}`
              : `OK — ${formatUsd(run.bufferRemaining)} to spare`}
          </p>
        </div>

        <div className={`${styles.gauge} ${run.solvent ? styles.gaugeGood : styles.gaugeBad}`}>
          <h4 className={styles.panelHead}>Solvency — worth more than it owes?</h4>
          <div className={styles.meter} aria-hidden="true">
            <div
              className={`${styles.meterFill} ${run.solvent ? '' : styles.meterFillBad}`}
              style={{ width: `${Math.max(0, Math.min(100, equityStartPct))}%` }}
            />
          </div>
          <p className={styles.gaugeReadout}>Equity {formatUsd(run.equity)}</p>
          <p className={styles.verdict}>
            {run.solvent ? 'SOLVENT — equity positive' : 'INSOLVENT'}
          </p>
        </div>
      </div>

      {allowHaircut && (
        <label className={styles.haircut}>
          <input
            type="checkbox"
            checked={haircut}
            onChange={(e) => commitHaircut(e.target.checked)}
          />
          Fire sale: dump securities below par (severe stress can dent equity too)
        </label>
      )}

      <p className={styles.explain} role="status" aria-live="polite">
        {explainRun(run)}
      </p>

      <p className={`${styles.task} ${completed ? styles.taskDone : ''}`}>
        <span className={styles.mark} aria-hidden="true">
          {completed ? '✓' : '•'}
        </span>
        <span>
          <strong>Task:</strong> {task}
          {completed
            ? ' Done — you pushed the run past the buffer and the bank failed on liquidity while still solvent.'
            : ''}
        </span>
      </p>
    </section>
  )
}

function Line({
  label,
  value,
  note,
  warn,
}: {
  label: string
  value: number
  note?: string
  warn?: boolean
}) {
  return (
    <div className={`${styles.line} ${warn ? styles.lineWarn : ''}`}>
      <span>{label}</span>
      <span className={styles.lineRight}>
        <span className={styles.value}>{formatUsd(value)}</span>
        {note ? <span className={styles.note}>{note}</span> : null}
      </span>
    </div>
  )
}

// No static fallback is needed: the lab has no required config and always
// renders a valid run against the fixed Cedar River model.
